import { FieldMultilineModalBase } from '@shared/blockly/fields';

const BUTTON_SIZE = 16;
const BUTTON_MARGIN = 4;

/**
 * Custom Blockly field that extends FieldTextInput with an additional
 * button (↗) to open a multiline modal editor. Normal clicks still
 * open the standard inline single-line editor.
 */
export class FieldMultilineModal extends FieldMultilineModalBase {
  private overlay_: HTMLDivElement | null = null;
  private dialog_: HTMLDivElement | null = null;
  private textarea_: HTMLTextAreaElement | null = null;
  private expandButton_: SVGGElement | null = null;

  public override initView(): void {
    super.initView();
    this.createExpandButton_();
  }

  private createExpandButton_(): void {
    if (!this.fieldGroup_) return;

    const group = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    group.setAttribute('class', 'blocklyMultilineBtn');
    group.setAttribute('cursor', 'pointer');

    const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    rect.setAttribute('rx', '3');
    rect.setAttribute('ry', '3');
    rect.setAttribute('width', String(BUTTON_SIZE));
    rect.setAttribute('height', String(BUTTON_SIZE));
    rect.setAttribute('fill', '#fff');
    rect.setAttribute('fill-opacity', '0.6');
    rect.setAttribute('stroke', '#888');
    rect.setAttribute('stroke-width', '0.5');
    group.appendChild(rect);

    const label = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    label.setAttribute('x', String(BUTTON_SIZE / 2));
    label.setAttribute('y', String(BUTTON_SIZE / 2 + 1));
    label.setAttribute('text-anchor', 'middle');
    label.setAttribute('dominant-baseline', 'central');
    label.setAttribute('font-size', '10');
    label.setAttribute('fill', '#555');
    label.setAttribute('pointer-events', 'none');
    label.textContent = '↗';
    group.appendChild(label);

    this.fieldGroup_.appendChild(group);
    this.expandButton_ = group;
  }

  protected override showEditor_(e?: Event): void {
    const value = this.getValue() ?? '';
    if ((e && this.expandButton_?.contains(e.target as Node)) || value.includes('\n')) {
      this.openMultilineModal_();
      return;
    }
    super.showEditor_(e);
  }

  protected override updateSize_(margin?: number): void {
    super.updateSize_(margin);
    this.size_.width += BUTTON_SIZE + BUTTON_MARGIN * 2;
  }

  protected override render_(): void {
    super.render_();
    this.positionExpandButton_();
  }

  private positionExpandButton_(): void {
    if (!this.expandButton_ || !this.borderRect_) return;
    const bbox = this.borderRect_.getBBox();
    const x = bbox.width + BUTTON_MARGIN;
    const y = (bbox.height - BUTTON_SIZE) / 2;
    this.expandButton_.setAttribute('transform', 'translate(' + String(x) + ', ' + String(y) + ')');
  }

  private openMultilineModal_(): void {
    const currentValue = this.getValue() ?? '';

    // Create overlay
    const overlay = document.createElement('div');
    overlay.style.cssText = `
      position: fixed; top: 0; left: 0; width: 100vw; height: 100vh;
      background: rgba(0,0,0,0.5); z-index: 10000;
      display: flex; align-items: center; justify-content: center;
    `;
    this.overlay_ = overlay;

    // Create dialog
    const dialog = document.createElement('div');
    dialog.style.cssText = `
      background: #fff; border-radius: 8px; padding: 20px;
      box-shadow: 0 8px 32px rgba(0,0,0,0.3);
      min-width: 500px; max-width: 80vw; max-height: 80vh;
      display: flex; flex-direction: column; gap: 12px;
    `;
    this.dialog_ = dialog;

    // Title
    const title = document.createElement('div');
    title.textContent = 'Mehrzeiliger Text';
    title.style.cssText = 'font-size: 16px; font-weight: 600; color: #333;';
    dialog.appendChild(title);

    // Textarea
    const textarea = document.createElement('textarea');
    textarea.value = currentValue;
    textarea.style.cssText = `
      width: 100%; min-height: 300px; padding: 12px;
      font-family: 'Fira Code', 'Consolas', 'Monaco', monospace;
      font-size: 14px; line-height: 1.5;
      border: 1px solid #ccc; border-radius: 4px;
      resize: both; outline: none; box-sizing: border-box;
    `;
    textarea.addEventListener('focus', () => {
      textarea.style.borderColor = '#4C97FF';
      textarea.style.boxShadow = '0 0 0 2px rgba(76,151,255,0.2)';
    });
    textarea.addEventListener('blur', () => {
      textarea.style.borderColor = '#ccc';
      textarea.style.boxShadow = 'none';
    });
    this.textarea_ = textarea;
    dialog.appendChild(textarea);

    // Button row
    const buttonRow = document.createElement('div');
    buttonRow.style.cssText = 'display: flex; justify-content: flex-end; gap: 8px;';

    const cancelBtn = document.createElement('button');
    cancelBtn.textContent = 'Abbrechen';
    cancelBtn.style.cssText = `
      padding: 8px 20px; border: 1px solid #ccc; border-radius: 4px;
      background: #fff; color: #333; font-size: 14px; cursor: pointer;
    `;
    cancelBtn.addEventListener('click', () => { this.closeModal_(false); });
    cancelBtn.addEventListener('mouseenter', () => { cancelBtn.style.background = '#f5f5f5'; });
    cancelBtn.addEventListener('mouseleave', () => { cancelBtn.style.background = '#fff'; });

    const saveBtn = document.createElement('button');
    saveBtn.textContent = 'Speichern';
    saveBtn.style.cssText = `
      padding: 8px 20px; border: none; border-radius: 4px;
      background: #4C97FF; color: #fff; font-size: 14px; cursor: pointer;
    `;
    saveBtn.addEventListener('click', () => { this.closeModal_(true); });
    saveBtn.addEventListener('mouseenter', () => { saveBtn.style.background = '#3A78D0'; });
    saveBtn.addEventListener('mouseleave', () => { saveBtn.style.background = '#4C97FF'; });

    buttonRow.appendChild(cancelBtn);
    buttonRow.appendChild(saveBtn);
    dialog.appendChild(buttonRow);

    // ESC / Ctrl+S key handler
    overlay.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        this.closeModal_(false);
      } else if (e.key === 's' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        this.closeModal_(true);
      }
    });

    // Click on overlay background closes
    overlay.addEventListener('mousedown', (e) => {
      if (e.target === overlay) {
        this.closeModal_(false);
      }
    });

    overlay.appendChild(dialog);
    document.body.appendChild(overlay);

    // Focus textarea
    requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(textarea.value.length, textarea.value.length);
    });
  }

  /**
   * Close the modal dialog and optionally save the value.
   */
  private closeModal_(save: boolean): void {
    if (save && this.textarea_) {
      this.setValue(this.textarea_.value);
    }

    if (this.overlay_) {
      this.overlay_.remove();
      this.overlay_ = null;
    }
    this.dialog_ = null;
    this.textarea_ = null;
  }

}
